import React, { useEffect, useMemo, useState, useContext } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Divider,
  Badge,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Verified,
  AccountCircle,
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  Security as SecurityIcon,
  Leaderboard as LeaderboardIcon,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { ColorModeContext } from "../theme/ThemeContext";
import { Brightness4, Brightness7 } from "@mui/icons-material";

const drawerWidth = 240;

export default function Sidebar({ onLogout, unreadCount = 0 }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { toggleColorMode, mode } = useContext(ColorModeContext);

  // ----- Guest detection (same robust logic) -----
  const [localGuest, setLocalGuest] = useState(() => {
    const v1 = localStorage.getItem("guest");
    const v2 = localStorage.getItem("guest_mode");
    return v1 === "1" || v2 === "true";
  });
  const [isAnon, setIsAnon] = useState(false);
  const [missingEmail, setMissingEmail] = useState(false);

  useEffect(() => {
    let alive = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!alive) return;
      const user = data?.session?.user;
      setIsAnon(!!user?.is_anonymous);
      setMissingEmail(!user?.email);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      const user = session?.user;
      setIsAnon(!!user?.is_anonymous);
      setMissingEmail(!user?.email);
    });

    const syncFlag = () => {
      const v1 = localStorage.getItem("guest");
      const v2 = localStorage.getItem("guest_mode");
      setLocalGuest(v1 === "1" || v2 === "true");
    };
    window.addEventListener("guest-mode-change", syncFlag);
    window.addEventListener("storage", syncFlag);

    return () => {
      alive = false;
      sub?.subscription?.unsubscribe?.();
      window.removeEventListener("guest-mode-change", syncFlag);
      window.removeEventListener("storage", syncFlag);
    };
  }, []);

  const isGuest = localGuest || isAnon || missingEmail;

  // ----- Nav items (mark private) -----
  const ALL_ITEMS = useMemo(
    () => [
      {
        text: "Dashboard",
        icon: <DashboardIcon />,
        path: "/dashboard",
        private: false,
      },
      {
        text: "Leaderboard",
        icon: <LeaderboardIcon />,
        path: "/leaderboard",
        private: false,
      },
      {
        text: "Profile",
        icon: <AccountCircle />,
        path: "/profile",
        private: true,
      },
      {
        text: "Notifications",
        icon: <NotificationsIcon />,
        path: "/notifications",
        private: true,
        withBadge: true,
      },
      { text: "Verify", icon: <Verified />, path: "/verify", private: false },
    ],
    []
  );

  // In guest mode, we still render ALL items; private ones are "gated"
  const itemsToRender = ALL_ITEMS;
  const isActive = (p) => location.pathname === p;

  // ----- Gated modal -----
  const [gateOpen, setGateOpen] = useState(false);
  const [gateTarget, setGateTarget] = useState(null); // "Profile" | "Notifications"

  const openGate = (label) => {
    setGateTarget(label);
    setGateOpen(true);
  };

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
            bgcolor: "background.paper",
            borderRight: "none",
          },
        }}
      >
        {/* Header */}
        <Toolbar sx={{ p: 2 }}>
          <Box
            onClick={() => navigate("/about-team")}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            <SecurityIcon
              sx={{ mr: 1.5, color: "primary.main", fontSize: 28 }}
            />
            <Typography
              variant="h6"
              sx={{ color: "text.primary", fontWeight: "bold" }}
            >
              AISecurityPulse
            </Typography>
          </Box>
        </Toolbar>

        <Divider />

        {/* Menu */}
        <List sx={{ p: 1 }}>
          {itemsToRender.map(
            ({ text, icon, path, withBadge, private: isPrivate }) => {
              const gated = isGuest && isPrivate;
              return (
                <ListItem key={text} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    selected={isActive(path)}
                    onClick={() => (gated ? openGate(text) : navigate(path))}
                    sx={{
                      borderRadius: 2,
                      opacity: gated ? 0.5 : 1,
                      cursor: gated ? "not-allowed" : "pointer",
                      "&.Mui-selected": {
                        backgroundColor: alpha("#2563EB", 0.08),
                        "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
                          color: "primary.main",
                          fontWeight: "500",
                        },
                      },
                      // keep clicks enabled even when visually "disabled"
                      pointerEvents: "auto",
                    }}
                  >
                    <ListItemIcon sx={{ color: "text.secondary" }}>
                      {withBadge && !isGuest ? (
                        <Badge
                          badgeContent={unreadCount}
                          color="error"
                          max={99}
                          overlap="circular"
                          invisible={!unreadCount}
                        >
                          <NotificationsIcon />
                        </Badge>
                      ) : (
                        icon
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={text}
                      sx={{ color: "text.primary" }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            }
          )}
        </List>

        <Box sx={{ flexGrow: 1 }} />
        <Divider />

        {/* Bottom action */}
        <List sx={{ p: 1 }}>
          <ListItem disablePadding>
            <ListItemButton onClick={toggleColorMode} sx={{ borderRadius: 2 }}>
              <ListItemIcon sx={{ color: "text.secondary" }}>
                {mode === "dark" ? <Brightness7 /> : <Brightness4 />}
              </ListItemIcon>
              <ListItemText
                primary={mode === "dark" ? "Light Mode" : "Dark Mode"}
                sx={{ color: "text.primary" }}
              />
            </ListItemButton>
          </ListItem>

          {isGuest ? (
            <ListItem disablePadding>
              <ListItemButton
                onClick={async () => {
                  navigate("/about-team");
                  try {
                    await supabase.auth.signOut();
                  } catch {}
                  localStorage.removeItem("guest");
                  localStorage.removeItem("guest_mode");
                  window.dispatchEvent(new Event("guest-mode-change"));
                }}
                sx={{ borderRadius: 2 }}
              >
                <ListItemIcon sx={{ color: "primary.main" }}>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Exit guest"
                  sx={{ color: "text.primary" }}
                />
              </ListItemButton>
            </ListItem>
          ) : (
            onLogout && (
              <ListItem disablePadding>
                <ListItemButton onClick={onLogout} sx={{ borderRadius: 2 }}>
                  <ListItemIcon sx={{ color: "error.main" }}>
                    <LogoutIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Logout"
                    sx={{ color: "text.primary" }}
                  />
                </ListItemButton>
              </ListItem>
            )
          )}
        </List>
      </Drawer>

      {/* Gate dialog */}
      <Dialog open={gateOpen} onClose={() => setGateOpen(false)}>
        <DialogTitle>Sign in to access</DialogTitle>
        <DialogContent>
          You need to sign in to use <strong>{gateTarget}</strong>.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGateOpen(false)}>Close</Button>
          <Button
            onClick={() => {
              setGateOpen(false);
              navigate("/login");
            }}
          >
            Sign in
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              navigate("/register");
              setGateOpen(false);
              try {
                await supabase.auth.signOut();
              } catch {}
              localStorage.removeItem("guest");
              localStorage.removeItem("guest_mode");
              window.dispatchEvent(new Event("guest-mode-change"));
            }}
          >
            Sign up
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
